import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useGetPermissionsQuery } from "@/features/roles-permissions/infrastructure/api/RolesApi";
import {
  useGetUserPermissionsQuery,
  useUpdateUserPermissionsMutation,
} from "../../infrastructure/api/membersGroupsApi";
import type { Member } from "../../domain/entities/Member";
import type { PermissionCategoryDTO } from "@/features/roles-permissions/domain/dto/RolesPermissionsDTO";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: Member | null;
}

interface PermissionEdits {
  memberId: string | null;
  added: Set<string>;
  removed: Set<string>;
}

export const ExtraPermissionsDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  member,
}) => {
  const { t } = useTranslation("membersGroups");
  const [edits, setEdits] = useState<PermissionEdits>({
    memberId: null,
    added: new Set(),
    removed: new Set(),
  });

  const {
    data: categories = [],
    isLoading: loadingAll,
    isError: allPermissionsError,
    isSuccess: allPermissionsLoaded,
  } = useGetPermissionsQuery(undefined, { skip: !open });

  const {
    data: permissionsData,
    isLoading: loadingUser,
    isError: userPermissionsError,
  } = useGetUserPermissionsQuery(member?.id ?? "", {
    skip: !open || !member || !allPermissionsLoaded,
    refetchOnMountOrArgChange: true,
  });

  const userPermissions = useMemo(
    () => permissionsData?.permissions ?? [],
    [permissionsData?.permissions],
  );

  const [updatePermissions, { isLoading: saving }] =
    useUpdateUserPermissionsMutation();

  const existingSlugs = useMemo(
    () => new Set(userPermissions.map((permission) => permission.slug)),
    [userPermissions],
  );
  const availableScopes = useMemo(
    () =>
      new Map(
        categories.flatMap((category) =>
          category.permissions.flatMap((permission) =>
            permission.slug
              ? [[permission.slug, permission.scope] as const]
              : [],
          ),
        ),
      ),
    [categories],
  );
  const existingScopes = useMemo(
    () =>
      new Map(
        userPermissions.map((permission) => [
          permission.slug,
          permission.scope,
        ]),
      ),
    [userPermissions],
  );

  const selected = useMemo(() => {
    const next = new Set(existingSlugs);
    if (edits.memberId !== member?.id) return next;

    edits.added.forEach((slug) => next.add(slug));
    edits.removed.forEach((slug) => next.delete(slug));
    return next;
  }, [edits, existingSlugs, member?.id]);

  const updateSelection = (next: Set<string>) => {
    const added = new Set(
      Array.from(next).filter((slug) => !existingSlugs.has(slug)),
    );

    const removed = new Set(
      Array.from(existingSlugs).filter((slug) => !next.has(slug)),
    );

    setEdits({
      memberId: member?.id ?? null,
      added,
      removed,
    });
  };

  const toggle = (slug: string) => {
    const next = new Set(selected);
    if (next.has(slug)) {
      next.delete(slug);
    } else {
      next.add(slug);
    }

    updateSelection(next);
  };

  const toggleCategory = (category: PermissionCategoryDTO) => {
    const slugs = category.permissions.flatMap((permission) =>
      permission.slug ? [permission.slug] : [],
    );
    const allSelected = slugs.every((slug) => selected.has(slug));

    const next = new Set(selected);
    slugs.forEach((slug) => {
      if (allSelected) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
    });
    updateSelection(next);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setEdits({ memberId: null, added: new Set(), removed: new Set() });
    }
    onOpenChange(nextOpen);
  };

  const permissionIdMap = useMemo(
    () =>
      new Map(
        categories.flatMap((category) =>
          category.permissions
            .filter(
              (
                permission,
              ): permission is typeof permission & {
                slug: string;
              } => Boolean(permission.slug),
            )
            .map((permission) => [permission.slug, permission.id] as const),
        ),
      ),
    [categories],
  );

  const handleSave = async () => {
    if (!member) return;

    const grant_permission_ids = Array.from(edits.added)
      .map((slug) => permissionIdMap.get(slug))
      .filter((id): id is string => Boolean(id));

    const revoke_permission_ids = Array.from(edits.removed)
      .map((slug) => permissionIdMap.get(slug))
      .filter((id): id is string => Boolean(id));

    try {
      await updatePermissions({
        userId: member.id,
        grant_permission_ids,
        revoke_permission_ids,
      }).unwrap();

      toast.success(t("extraPermissionsDialog.success"));
      handleOpenChange(false);
    } catch (error) {
      console.error("Permission update failed", error);
      toast.error(t("extraPermissionsDialog.error"));
    }
  };

  const isLoading = loadingAll || loadingUser;
  const hasError = allPermissionsError || userPermissionsError;
  const hasChanges =
    selected.size !== existingSlugs.size ||
    Array.from(selected).some((slug) => !existingSlugs.has(slug));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] min-h-0 flex-col overflow-hidden rounded-xl sm:max-h-[80vh] sm:max-w-[560px]">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-lg font-semibold">
            {t("extraPermissionsDialog.title")}
          </DialogTitle>
          {member && (
            <p className="text-sm text-gray-500">
              {t("extraPermissionsDialog.subtitle", { name: member.name })}
            </p>
          )}
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto py-2 pr-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : hasError ? (
            <p className="py-10 text-center text-sm text-red-500">
              {t("extraPermissionsDialog.loadError")}
            </p>
          ) : categories.length === 0 ? (
            <p className="text-sm text-center text-gray-500 py-10">
              {t("extraPermissionsDialog.empty")}
            </p>
          ) : (
            <div className="space-y-5">
              {categories.map((cat) => {
                const selectedCount = cat.permissions.filter((permission) =>
                  permission.slug ? selected.has(permission.slug) : false,
                ).length;
                const categoryChecked =
                  cat.permissions.length > 0 &&
                  selectedCount === cat.permissions.length;
                const categoryState =
                  selectedCount > 0 && !categoryChecked
                    ? "indeterminate"
                    : categoryChecked;

                return (
                  <div key={cat.category}>
                    <label className="mb-2 flex cursor-pointer items-center gap-2 border-b pb-2">
                      <Checkbox
                        checked={categoryState}
                        onCheckedChange={() => toggleCategory(cat)}
                        disabled={saving || cat.permissions.length === 0}
                      />
                      <span className="text-sm font-semibold text-gray-700">
                        {cat.category}
                      </span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {cat.permissions.map((perm) => (
                        <label
                          key={perm.id}
                          className="flex items-center gap-2 cursor-pointer select-none"
                        >
                          <Checkbox
                            checked={Boolean(
                              perm.slug && selected.has(perm.slug),
                            )}
                            onCheckedChange={() =>
                              perm.slug && toggle(perm.slug)
                            }
                            disabled={saving || !perm.slug}
                          />
                          <span className="text-sm text-gray-600">
                            {perm.generic_name ?? perm.name ?? perm.slug}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 shrink-0 border-t">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={saving}
            className="px-4 py-5 text-primary border-primary hover:text-primary hover:bg-white cursor-pointer"
          >
            {t("extraPermissionsDialog.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || hasError || !hasChanges}
            loading={saving}
            className="bg-primary hover:bg-primary text-white px-4 py-5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("extraPermissionsDialog.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
